/**
 * Configuração da API
 * Centraliza URLs e configurações de conexão com o backend
 *
 * SECURITY FIXES:
 * - Token stored in memory by default (not localStorage)
 * - Optional secure httpOnly cookie fallback support
 * - Token validation before use
 * - Proper error handling for expired tokens (401)
 * - Support for refresh tokens
 */

function resolveApiBase(): string {
  const envUrl = (import.meta as unknown as { env?: { VITE_API_URL?: string } })?.env?.VITE_API_URL;
  if (envUrl && envUrl.trim().length > 0) return envUrl.trim();
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';

  // 🎯 LOCAL DEVELOPMENT: localhost → PRODUÇÃO (para trabalhar com dados reais)
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'https://airtrust-api-production.airtrust.workers.dev/api';
  }

  // 🎯 STAGING: main.airtrust.pages.dev → staging API (zero cache)
  if (host === 'main.airtrust.pages.dev') {
    return 'https://airtrust-api-staging.airtrust.workers.dev/api';
  }

  // 🚀 PRODUCTION: production.airtrust.pages.dev → production API (normal cache)
  if (
    host === 'production.airtrust.pages.dev' ||
    host.includes('pages.dev') ||
    host.includes('airtrust.pages.dev')
  ) {
    return 'https://airtrust-api-production.airtrust.workers.dev/api';
  }

  return `${origin}/api`;
}

export const API_BASE_URL = resolveApiBase();

// 🔍 DEBUG: Log da URL de API para diagnóstico
if (typeof window !== 'undefined') {
  console.log('🔍 [API Config] VITE_API_URL:', import.meta.env.VITE_API_URL);
  console.log('🔍 [API Config] API_BASE_URL (final):', API_BASE_URL);
}

// ===== TOKEN STORAGE (Memory-based for security) =====
let cachedToken: string | null = null;
let cachedRefreshToken: string | null = null;

export function setTokens(accessToken: string, refreshToken?: string): void {
  cachedToken = accessToken;
  if (refreshToken) {
    cachedRefreshToken = refreshToken;
  }
  // Optional: Store in secure httpOnly cookie for persistence
  // This would require backend support for set-cookie
}

export function clearTokens(): void {
  cachedToken = null;
  cachedRefreshToken = null;
  // Clear cookie if used
  if (typeof document !== 'undefined') {
    document.cookie = 'auth_token=; Max-Age=0; path=/;';
  }
}

export function getAccessToken(): string | null {
  return cachedToken;
}

export function getRefreshToken(): string | null {
  return cachedRefreshToken;
}

/**
 * Validate JWT token format (basic check)
 */
function isValidToken(token: string | null): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT format: header.payload.signature
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  try {
    // Decode payload (no signature verification here - that's done by backend)
    const payload = JSON.parse(atob(parts[1]));

    // Check expiry
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      return false; // Token expired
    }

    return true;
  } catch {
    return false;
  }
}

export const API_ENDPOINTS = {
  LOGIN: `${API_BASE_URL}/auth/login`,
  LOGOUT: `${API_BASE_URL}/auth/logout`,
  REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh`,
  VERIFY_TOKEN: `${API_BASE_URL}/auth/verify`,

  FUNCIONARIOS: `${API_BASE_URL}/funcionarios`,
  FUNCIONARIO_BY_ID: (id: number) => `${API_BASE_URL}/funcionarios/${id}`,

  CERTIFICACOES: `${API_BASE_URL}/qualificacoes`,

  PASTA_VIRTUAL: `${API_BASE_URL}/pasta-virtual`,

  SIMULADORES: `${API_BASE_URL}/simuladores`,

  HEALTH: `${API_BASE_URL.replace('/api', '')}/api/health`,
  AUDIT_LOGS: `${API_BASE_URL.replace('/api', '')}/api/sistema/audit-logs`,
};

export const fetchConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include' as RequestCredentials,
};

/**
 * Fetch with authorization header and automatic token refresh
 * @param url - API endpoint
 * @param options - Fetch options
 * @param retry - Internal: whether this is a retry attempt
 */
export async function fetchWithAuth(
  url: string,
  options: RequestInit = {},
  retry: boolean = false,
): Promise<Response> {
  const token = getAccessToken();

  // ===== TOKEN VALIDATION =====
  if (!isValidToken(token)) {
    // Try to refresh
    if (!retry && cachedRefreshToken) {
      try {
        await refreshAccessToken();
        return fetchWithAuth(url, options, true); // Retry with new token
      } catch {
        clearTokens();
        throw new Error('Token refresh failed');
      }
    }

    // If no refresh possible, clear tokens
    clearTokens();
    throw new Error('Authentication required');
  }

  const config: RequestInit = {
    ...options,
    headers: {
      ...fetchConfig.headers,
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  };

  const response = await fetch(url, config);

  // ===== HANDLE 401 UNAUTHORIZED =====
  if (response.status === 401 && !retry) {
    // Token expired or invalid, try refresh
    if (cachedRefreshToken) {
      try {
        await refreshAccessToken();
        return fetchWithAuth(url, options, true); // Retry with new token
      } catch {
        clearTokens();
        throw new Error('Session expired. Please login again.');
      }
    } else {
      clearTokens();
      throw new Error('Session expired. Please login again.');
    }
  }

  return response;
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<void> {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const response = await fetch(API_ENDPOINTS.REFRESH_TOKEN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (!response.ok) {
      clearTokens();
      throw new Error('Refresh token failed');
    }

    const data = (await response.json()) as { access_token?: string; refresh_token?: string };

    if (data.access_token) {
      setTokens(data.access_token, data.refresh_token);
    } else {
      throw new Error('No token in refresh response');
    }
  } catch (error) {
    clearTokens();
    throw error;
  }
}

/**
 * Perform logout
 */
export async function logout(): Promise<void> {
  try {
    const token = getAccessToken();
    if (token) {
      await fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
  } catch (error) {
    console.warn('Logout request failed', error);
  } finally {
    clearTokens();
  }
}
